import { useTranslation } from 'react-i18next';
import { categoryLabel, categoryOptionLabel } from '@/lib/category';
import type { CategoryOption } from '@/types';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { SourceBadge, StatusBadge } from '@/components/admin/EventBadges';
import { formatAdminEventDate } from '@/lib/adminEvent';
import {
  AdminApiError,
  fetchAdminCategoryOptions,
  fetchAdminEvents,
  setAdminEventVisibility,
} from '@/services/adminApi';
import { formatSqliteUtcDateTime } from '@/lib/date';
import type { AdminEvent, AdminEventPage, EventFilters } from '@/types/admin';

const SELECT_CLASS = 'field-control h-9 w-auto';

function readFilters(params: URLSearchParams): EventFilters {
  const page = Number(params.get('page') ?? '1');
  return {
    q: params.get('q') ?? '',
    type: (params.get('type') ?? '') as EventFilters['type'],
    category: params.get('category') ?? '',
    status: (params.get('status') ?? '') as EventFilters['status'],
    source: (params.get('source') ?? '') as EventFilters['source'],
    page: Number.isInteger(page) && page > 0 ? page : 1,
  };
}

function EventsTable() {
  const { t } = useTranslation('admin');
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = readFilters(searchParams);
  const [query, setQuery] = useState(filters.q);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [categoriesLoadFailed, setCategoriesLoadFailed] = useState(false);
  const [page, setPage] = useState<AdminEventPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const paramsKey = searchParams.toString();

  useEffect(() => {
    const controller = new AbortController();
    fetchAdminCategoryOptions(controller.signal)
      .then(setCategories)
      .catch(() => {
        if (!controller.signal.aborted) setCategoriesLoadFailed(true);
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    fetchAdminEvents(readFilters(new URLSearchParams(paramsKey)), controller.signal)
      .then(setPage)
      .catch((reason: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        let message = 'events.loadFailed';
        if (reason instanceof AdminApiError) {
          message = reason.message;
        }
        setError(message);
      });
    return () => controller.abort();
  }, [paramsKey, reloadToken]);

  const updateFilters = (changes: Partial<EventFilters>) => {
    const next = { ...filters, ...changes };
    const category = categories.find((option) => (
      [option.slug, option.name_zh, option.name_en].includes(next.category)
    ));
    if (category) {
      next.category = category.slug;
    }
    // 任何筛选变化都回到第一页；翻页本身除外。
    if (changes.page === undefined) {
      next.page = 1;
    }
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(next)) {
      if (value !== '' && !(key === 'page' && value === 1)) {
        params.set(key, String(value));
      }
    }
    setSearchParams(params);
  };

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    updateFilters({ q: query.trim() });
  };

  const toggleVisibility = async (item: AdminEvent) => {
    const hiding = item.is_enabled === 1;
    if (hiding && !window.confirm(t('events.hideNamedConfirm', { title: item.title }))) {
      return;
    }
    setBusyId(item.id);
    try {
      await setAdminEventVisibility(item.id, !hiding);
      setReloadToken((token) => token + 1);
    } catch (reason) {
      let message = 'events.actionFailed';
      if (reason instanceof AdminApiError) {
        message = reason.message;
      }
      setError(message);
    } finally {
      setBusyId(null);
    }
  };

  let totalPages = 1;
  if (page) {
    totalPages = Math.max(1, Math.ceil(page.total / page.page_size));
  }

  const selectedCategoryOption = categories.find((option) => (
    [option.slug, option.name_zh, option.name_en].includes(filters.category)
  ));
  let categoryFilterValue = filters.category;
  if (selectedCategoryOption) {
    categoryFilterValue = selectedCategoryOption.slug;
  }

  let body = (
    <p className="kicker m-0 py-12" aria-live="polite">{t('loading')}</p>
  );
  if (page && page.items.length === 0) {
    body = <p className="m-0 py-12 text-sm text-ink-2">{t('events.empty')}</p>;
  } else if (page) {
    body = (
      <div className="overflow-x-auto">
        <table className="table-editorial">
          <thead>
            <tr>
              <th>{t('fields.title')}</th>
              <th className="whitespace-nowrap">{t('fields.date')}</th>
              <th>{t('fields.category')}</th>
              <th>{t('fields.source')}</th>
              <th>{t('fields.status')}</th>
              <th className="whitespace-nowrap">{t('fields.updated_at')}</th>
              <th className="pr-0 text-right">{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {page.items.map((item) => (
              <tr key={item.id}>
                <td className="min-w-[200px]">
                  <Link to={`/admin/events/${item.id}`} className="text-ink hover:text-accent-text">
                    {item.title}
                  </Link>
                  {item.location && (
                    <div className="mt-0.5 text-xs text-ink-2">{item.location}</div>
                  )}
                </td>
                <td className="whitespace-nowrap text-ink-2">
                  {formatAdminEventDate(item)}
                </td>
                <td className="whitespace-nowrap">{categoryLabel(item) || '—'}</td>
                <td className="whitespace-nowrap">
                  <SourceBadge event={item} />
                </td>
                <td>
                  <StatusBadge enabled={item.is_enabled === 1} />
                </td>
                <td className="whitespace-nowrap font-mono text-xs text-ink-2">
                  {formatSqliteUtcDateTime(item.updated_at)}
                </td>
                <td className="whitespace-nowrap pr-0 text-right">
                  <Button asChild variant="ghost" size="sm">
                    <Link to={`/admin/events/${item.id}`}>{t('events.editAction')}</Link>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busyId === item.id}
                    onClick={() => toggleVisibility(item)}
                  >
                    {t(item.is_enabled === 1 ? 'events.hideAction' : 'events.showAction')}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div>
      <header className="flex flex-wrap items-end justify-between gap-3 border-b border-rule pb-5">
        <h1 className="m-0 text-[1.75rem] md:text-[2rem]">{t('events.title')}</h1>
        <Button asChild size="sm">
          <Link to="/admin/events/new">{t('events.new')}</Link>
        </Button>
      </header>

      <form onSubmit={handleSearch} className="mt-5 flex flex-wrap items-center gap-2">
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('events.searchPlaceholder')}
          className="h-9 min-w-[220px] flex-1"
          aria-label={t('events.searchLabel')}
        />
        <select
          className={SELECT_CLASS}
          value={filters.type}
          onChange={(event) => updateFilters({ type: event.target.value as EventFilters['type'] })}
          aria-label={t('fields.event_type')}
        >
          <option value="">{t('events.allTypes')}</option>
          <option value="annual">{t('events.annual')}</option>
          <option value="specific">{t('events.specific')}</option>
        </select>
        <select
          className={SELECT_CLASS}
          value={categoryFilterValue}
          onChange={(event) => updateFilters({ category: event.target.value })}
          aria-label={t('fields.category')}
        >
          <option value="">{t('events.allCategories')}</option>
          {categories.map((category) => (
            <option key={category.slug} value={category.slug}>
              {categoryOptionLabel(category)}
            </option>
          ))}
        </select>
        <select
          className={SELECT_CLASS}
          value={filters.source}
          onChange={(event) => updateFilters({ source: event.target.value as EventFilters['source'] })}
          aria-label={t('fields.source')}
        >
          <option value="">{t('events.allSources')}</option>
          <option value="seed">{t('sources.seed')}</option>
          <option value="crawler">{t('sources.crawler')}</option>
          <option value="manual">{t('sources.manual')}</option>
        </select>
        <select
          className={SELECT_CLASS}
          value={filters.status}
          onChange={(event) => updateFilters({ status: event.target.value as EventFilters['status'] })}
          aria-label={t('fields.status')}
        >
          <option value="">{t('events.allStatuses')}</option>
          <option value="visible">{t('status.visible')}</option>
          <option value="hidden">{t('status.hidden')}</option>
        </select>
        <Button type="submit" variant="outline" size="sm" className="h-9">
          {t('events.search')}
        </Button>
      </form>

      {categoriesLoadFailed && (
        <p role="alert" className="callout callout-warning m-0 mt-4">{t('form.categoriesLoadFailed')}</p>
      )}
      {error && (
        <p role="alert" className="callout callout-danger m-0 mt-4">
          {t(error, { defaultValue: error })}
        </p>
      )}

      <section className="mt-6 border-t-2 border-ink">
        {body}
      </section>

      {page && page.total > 0 && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-rule pt-3 text-xs text-ink-2">
          <span>
            {t('events.pagination', { count: page.total, page: page.page, totalPages })}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page.page <= 1}
              onClick={() => updateFilters({ page: page.page - 1 })}
              aria-label={t('events.previousPage')}
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page.page >= totalPages}
              onClick={() => updateFilters({ page: page.page + 1 })}
              aria-label={t('events.nextPage')}
            >
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminEventsPage() {
  return (
    <AdminLayout>
      <EventsTable />
    </AdminLayout>
  );
}
